import { useCallback } from "react";
import { VideoPlatform, VideoSDKAdapter } from "../video-sdk";
import { baseVideoSDKAdapterAtom } from "../video-sdk/core/atoms";
import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { ZoomVideoSDKAdapter } from "../video-sdk/zoom/adapter";
import { useIsZoomEnabled, useIsZoomEnabledCallback } from "./useIsZoomEnabled";

/**
 * Returns the current video platform, based on the initialized video SDK adapter.
 */
export function useVideoPlatform(): VideoPlatform | null {
  const videoSDKAdapter = useAtomValue(baseVideoSDKAdapterAtom);

  return getVideoPlatform(videoSDKAdapter);
}

export function useVideoPlatformCallback(): () => VideoPlatform | null {
  const getVideoSDKAdapter = useAtomCallback(
    useCallback((get) => get(baseVideoSDKAdapterAtom), []),
  );

  return useCallback(() => {
    const videoSDKAdapter = getVideoSDKAdapter();
    if (videoSDKAdapter === null) {
      return null;
    }
    return getVideoPlatform(videoSDKAdapter);
  }, [getVideoSDKAdapter]);
}

/**
 * Returns the desired video platform, based on the Statsig gate and any overrides..
 */
export function useDesiredVideoPlatform(): VideoPlatform {
  const isZoomEnabled = useIsZoomEnabled();
  const desiredVideoPlatform = getDesiredVideoPlatform(isZoomEnabled);

  return desiredVideoPlatform;
}

export function useDesiredVideoPlatformCallback(): () => VideoPlatform {
  const checkIsZoomEnabled = useIsZoomEnabledCallback();

  return useCallback(
    () => getDesiredVideoPlatform(checkIsZoomEnabled()),
    [checkIsZoomEnabled],
  );
}

// utils

export function getVideoPlatform(
  videoSDKAdapter: VideoSDKAdapter | null,
): VideoPlatform | null {
  if (!videoSDKAdapter) return null;

  return videoSDKAdapter instanceof ZoomVideoSDKAdapter
    ? VideoPlatform.Zoom
    : VideoPlatform.Twilio;
}

function getDesiredVideoPlatform(isZoomEnabled: boolean): VideoPlatform {
  return isZoomEnabled ? VideoPlatform.Zoom : VideoPlatform.Twilio;
}
